import { getQuery } from 'shared/utils/queryBuilder';
import { isRecentlyActive } from 'components/Layout/menuActivity';
import Dashboard from './Dashboard';

function compareDashboards(a, b) {
  const aIndex = a.settings?.sortIndex;
  const bIndex = b.settings?.sortIndex;
  const aHasIndex = aIndex != null;
  const bHasIndex = bIndex != null;

  if (aHasIndex && bHasIndex) {
    if (aIndex !== bIndex) return aIndex - bIndex;
  } else if (aHasIndex) {
    return -1;
  } else if (bHasIndex) {
    return 1;
  }

  return (a.title || '').localeCompare(b.title || '');
}

function sortDashboards(dashboards) {
  return [...dashboards].sort(compareDashboards);
}

// root=0; three nav levels => max depth index is 2
const MAX_DASHBOARD_NAV_DEPTH = 2;

function findDashboard(dashboards, id) {
  return dashboards.find(d => d.id === id);
}

export function getDashboardDepth(dashboardId, dashboards) {
  let depth = 0;
  let current = findDashboard(dashboards, dashboardId);
  while (current?.parent) {
    depth += 1;
    current = findDashboard(dashboards, current.parent);
  }
  return depth;
}

function isDashboardDescendant(dashboards, ancestorId, dashboardId) {
  let current = findDashboard(dashboards, dashboardId);
  while (current?.parent) {
    if (current.parent === ancestorId) return true;
    current = findDashboard(dashboards, current.parent);
  }
  return false;
}

export function getParentDashboardOptions(dashboardId, dashboards) {
  if (!dashboardId || !dashboards?.length) return [];

  return dashboards
    .filter(d => d.id !== dashboardId
      && !isDashboardDescendant(dashboards, dashboardId, d.id)
      && getDashboardDepth(d.id, dashboards) < MAX_DASHBOARD_NAV_DEPTH)
    .map(d => ({ label: d.title, value: d.id }));
}

function mapDashboardToRoute({ dashboard, props, dashboards, canEdit, matomoGoals, checkPermission }) {
  if (!checkPermission(dashboard?.settings?.viewPermission)) {
    return null;
  }

  const children = [];
  const editLinks = [];
  let hasGoalChildren = false;

  for (const d of sortDashboards(dashboards.filter(child => child.parent === dashboard.id && checkPermission(dashboard.settings?.viewPermission)))) {
    if (d.settings?.dynamicContent === 'MATOMO_GOALS') {
      hasGoalChildren = true;
      if (matomoGoals?.length) {
        children.push(...[...matomoGoals]
          .sort((a, b) => b.matomoGoalId - a.matomoGoalId)
          .map(g => ({
            path: g.matomoGoalId.toString(),
            label: g.name,
            active: isRecentlyActive(g.conversions?.[0]),
            component: Dashboard,
            props: {
              dashboardId: d.id,
              dashboardTitle: `Goal: ${g.name}`,
              dashboardFilter: getQuery({ field: 'conversionGoalId', value: g.id, operator: '=' }),
              ...props,
            },
          })));
      }
      editLinks.push({
        path: 'goals-template',
        label: 'Edit goals template',
        component: Dashboard,
        props: {
          dashboardId: d.id,
          ...props,
        },
      });
    } else {
      const childRoute = mapDashboardToRoute({ dashboard: d, props, dashboards, canEdit, matomoGoals, checkPermission });
      if (childRoute) {
        children.push(childRoute);
      }
    }
  }

  const isEmptyParent = !dashboard.contents?.length && children.length > 0;

  if (isEmptyParent && canEdit) {
    editLinks.push(
      {
        path: 'edit',
        label: 'Edit',
        component: Dashboard,
        props: {
          dashboardId: dashboard.id,
          ...props,
        },
      },
    );
  }

  if (editLinks.length && canEdit) {
    children.push({ type: 'divider' }, ...editLinks);
  }

  return {
    path: dashboard.id,
    label: dashboard.title,
    link: isEmptyParent ? false : undefined,
    component: Dashboard,
    props: {
      dashboardId: dashboard.id,
      ...props,
    },
    children,
    activeEmptyLabel: hasGoalChildren ? 'No active goals' : undefined,
  };
}

export function getDashboardRoutes({ dashboardPage, props, canEdit, matomoGoals, checkPermission = () => true }) {
  const rootDashboards = sortDashboards(dashboardPage?.dashboards?.filter(d => !d.parent) || []);
  return rootDashboards
    .map(d => mapDashboardToRoute({ dashboard: d, props, dashboards: dashboardPage.dashboards, canEdit, matomoGoals, checkPermission }))
    .filter(Boolean);
}

if (process.env.NODE_ENV !== 'production') {
  const fixtures = [
    { id: 'root-a', title: 'A', parent: null },
    { id: 'child-b', title: 'B', parent: 'root-a' },
    { id: 'grandchild-c', title: 'C', parent: 'child-b' },
    { id: 'root-d', title: 'D', parent: null },
  ];
  console.assert(getDashboardDepth('grandchild-c', fixtures) === 2);
  console.assert(getParentDashboardOptions('grandchild-c', fixtures).map(o => o.value).sort().join() === 'child-b,root-a,root-d');
  console.assert(getParentDashboardOptions('child-b', fixtures).map(o => o.value).sort().join() === 'root-a,root-d');
  console.assert(getParentDashboardOptions('root-a', fixtures).map(o => o.value).sort().join() === 'root-d');
}
